/**
 * useCredentialEvents Hook
 * 
 * Handles all credential-related WebSocket events:
 * - credentialStatusChanged: Update credential status across devices
 * - credentialProvisioned: Handle new credential provisioning
 * - credentialRemovedFromDevice: Handle credential deletion
 * - credentialDownloaded: Track download events
 * - patronUpdated: Sync patron data changes
 * 
 * Extracted from AppContent.js as part of Phase 3A refactoring
 * Goal: Remove ~500 lines of event handling code from AppContent
 * 
 * @module hooks/useCredentialEvents
 */

import { useCallback, useMemo } from 'react';
import { useCredentialsStore, useDevicesStore } from '../stores';
import logger from '../utils/logger';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get credential status store from localStorage
 * @returns {Object} Status store object mapping credentialId -> status
 */
export const getStatusStore = () => {
  try {
    const statuses = localStorage.getItem('credentialStatuses');
    return statuses ? JSON.parse(statuses) : {};
  } catch (e) {
    logger.error('[useCredentialEvents] Failed to parse status store:', e);
    return {};
  }
};

/**
 * Set credential status in localStorage
 * @param {string} credentialId - Credential ID
 * @param {string} status - Status value (e.g., "Active", "Suspended", "Deleted")
 */
export const setStatusInStore = (credentialId, status) => {
  try {
    const statuses = getStatusStore();
    statuses[credentialId] = status;
    localStorage.setItem('credentialStatuses', JSON.stringify(statuses));
  } catch (e) {
    logger.error('[useCredentialEvents] Failed to set status in store:', e);
  }
};

/**
 * Remove credential status from localStorage
 * @param {string} credentialId - Credential ID
 */
export const removeStatusFromStore = (credentialId) => {
  if (!credentialId) return;
  try {
    const statuses = getStatusStore();
    delete statuses[credentialId];
    localStorage.setItem('credentialStatuses', JSON.stringify(statuses));
  } catch (e) {
    logger.error('[useCredentialEvents] Failed to remove status from store:', e);
  }
};

/**
 * Create composite key from deviceId and credentialId
 * Format: "deviceId:credentialId"
 * @param {string} deviceId - Device ID
 * @param {string} credentialId - Credential ID
 * @returns {string} Composite key
 */
export const makePendingKey = (deviceId, credentialId) => {
  return `${deviceId}:${credentialId}`;
};

/**
 * Parse composite key back to deviceId and credentialId
 * @param {string} key - Composite key in format "deviceId:credentialId"
 * @returns {Object} Object with deviceId and credentialId properties
 */
export const parsePendingKey = (key) => {
  if (!key || typeof key !== 'string') {
    logger.warn('[useCredentialEvents] parsePendingKey received invalid key:', key);
    return { deviceId: null, credentialId: null };
  }
  const parts = key.split(':');
  if (parts.length === 0) {
    return { deviceId: null, credentialId: null };
  }
  if (parts.length === 1) {
    return { deviceId: parts[0], credentialId: undefined };
  }
  // Join everything after first colon back together to handle credential IDs with colons
  const deviceId = parts[0];
  const credentialId = parts.slice(1).join(':');
  return { deviceId, credentialId };
};

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Custom hook for handling credential-related WebSocket events
 * 
 * @param {Object} dependencies - Required dependencies from AppContent
 * @param {string} dependencies.selectedDeviceId - Currently selected device ID
 * @param {string} dependencies.installationId - Current installation ID
 * @param {Function} dependencies.authenticatedFetch - Authenticated fetch function
 * @param {Function} dependencies.fetchDevices - Function to fetch devices list
 * @param {Function} dependencies.fetchDeviceStateReadOnly - Function to fetch device state (read-only)
 * @param {Function} dependencies.invalidateDeviceStateCache - Function to invalidate device state cache
 * @param {Function} dependencies.setLastCredentialProvisioned - Setter for last provisioned event
 * @param {Function} dependencies.setLastCredentialDownload - Setter for last download event  
 * @param {Function} dependencies.setLastCredentialRemoved - Setter for last removed event
 * 
 * @returns {Object} Event handler functions for credential events
 */
export function useCredentialEvents(dependencies = {}) {
  const {
    selectedDeviceId,
    fetchDeviceStateReadOnly,
    invalidateDeviceStateCache,
    setLastCredentialProvisioned,
    setLastCredentialDownload,
    setLastCredentialRemoved,
    setActionHistory,
    showNotification,
  } = dependencies;

  // Access Zustand stores directly
  const {
    pendingDeletionCredentials,
    devicesWithPendingDeletions,
    setCredentials,
    setPendingDeletionCredentials,
    setPendingAdditionCredentials,
    setOfflineDeviceCredentials,
    setDevicesWithPendingDeletions,
    setPatronsMap,
  } = useCredentialsStore();

  const {
    deviceStates,
    setDeviceStates,
    updateDeviceState,
    updateMultipleDeviceStates,
  } = useDevicesStore();

  /**
   * Handle credentialStatusChanged event
   * Updates credential status across all affected devices
   * 
   * @param {Object} message - WebSocket message
   * @param {string} message.credentialId - Credential ID
   * @param {string} message.newStatus - New status (Active, Suspended, Deleted)
   * @param {string[]} message.deviceIds - Array of affected device IDs
   */
  const handleCredentialStatusChanged = useCallback((message) => {
    const { credentialId, newStatus, deviceIds } = message;
    logger.log(`[useCredentialEvents] credentialStatusChanged: credential ${credentialId} -> ${newStatus}`);
    
    // Check if this event is relevant to the currently selected device
    const relevantToCurrentDevice = deviceIds && deviceIds.includes(selectedDeviceId);
    
    // Update credentials UI state
    setCredentials((prevCreds) => {
      if (!relevantToCurrentDevice) return prevCreds;
      
      if (newStatus === "Deleted") {
        // Remove from localStorage status store
        removeStatusFromStore(credentialId);
        
        // Remove device-credential pair from pending deletion set
        setPendingDeletionCredentials((prev) => {
          const newSet = new Set(prev);
          // Remove all entries for this credential across all devices
          const keysToDelete = [...newSet].filter((key) => {
            if (!key || typeof key !== "string") return false;
            return parsePendingKey(key).credentialId === credentialId;
          });
          keysToDelete.forEach((key) => newSet.delete(key));
          return newSet;
        });
        
        // Remove credential from list
        return prevCreds.filter((c) => c.id !== credentialId);
      } else {
        // Update status in localStorage
        setStatusInStore(credentialId, newStatus);
        
        // Update credential in list
        return prevCreds.map((c) =>
          c.id === credentialId ? { ...c, status: newStatus } : c
        );
      }
    });

    // Also update deviceStates cache for all affected devices
    if (deviceIds && deviceIds.length > 0) {
      const updates = [];

      deviceIds.forEach((deviceId) => {
        const deviceData = deviceStates[deviceId];
        if (deviceData && deviceData.credentials) {
          const updatedCredentials = deviceData.credentials.map((cred) => {
            if (cred.id === credentialId) {
              logger.log(
                `[useCredentialEvents] Updating cache for device ${deviceId}, credential ${cred.id}: ${cred.status} -> ${newStatus}`
              );
              return { ...cred, status: newStatus };
            }
            return cred;
          });

          updates.push({
            deviceId,
            state: {
              ...deviceData,
              credentials: updatedCredentials,
              lastUpdated: Date.now(), // Mark as fresh from WebSocket
            },
          });
        }
      });

      if (updates.length > 0) {
        updateMultipleDeviceStates(updates);
      }
    }
  }, [
    selectedDeviceId,
    deviceStates,
    setCredentials,
    setPendingDeletionCredentials,
    updateMultipleDeviceStates,
  ]);

  /**
   * Handle credentialProvisioned event
   * Processes new credential additions across multiple devices
   * 
   * @param {Object} message - WebSocket message
   * @param {string} message.credentialId - Credential ID
   * @param {string[]} message.deviceIds - Array of device IDs receiving the credential
   * @param {Object} message.credential - Credential object with patronId
   * @param {string} message.patronId - Patron ID (may be at root or in credential)
   */
  const handleCredentialProvisioned = useCallback((message) => {
    const { deviceIds, credentialId } = message;
    
    if (!deviceIds || deviceIds.length === 0) {
      logger.warn('[useCredentialEvents] credentialProvisioned: No deviceIds in message:', message);
      return;
    }

    logger.log('[useCredentialEvents] credentialProvisioned:', message);
    logger.log(`[useCredentialEvents] Devices affected: ${deviceIds.join(', ')}`);
    logger.log(`[useCredentialEvents] Current device: ${selectedDeviceId}`);

    // Extract patronId from the credential object in the message
    const provisionedPatronId = message.patronId || message.credential?.patronId;
    if (provisionedPatronId) {
      logger.log(`[useCredentialEvents] PatronId ${provisionedPatronId} extracted from message`);
    }

    // Refresh state for ALL affected devices sequentially
    (async () => {
      // Read deviceStates snapshot at event-handling time (not as a reactive dep)
      const currentDeviceStates = useDevicesStore.getState().deviceStates;
      const currentPatronsMap = useCredentialsStore.getState().patronsMap;

      for (const deviceId of deviceIds) {
        logger.log(`[useCredentialEvents] Refreshing state for device ${deviceId}...`);

        // Invalidate cache before fetching fresh data
        invalidateDeviceStateCache(deviceId);

        try {
          const deviceState = await fetchDeviceStateReadOnly(deviceId);
          logger.log(`[useCredentialEvents] Fetched state for ${deviceId}:`, deviceState);

          if (deviceState) {
            // Update deviceStates cache for this device with fresh timestamp
            updateDeviceState(deviceId, {
              ...(currentDeviceStates[deviceId] || {}),
              ...deviceState,
              lastUpdated: Date.now(), // Mark as fresh from WebSocket
            });
            logger.log(
              `[useCredentialEvents] Updated deviceStates cache for ${deviceId} with timestamp ${Date.now()}`
            );

            // If this is the currently selected device, also update the UI state
            if (deviceId === selectedDeviceId) {
              logger.log('[useCredentialEvents] This IS the current device - updating UI');
              const statusStore = getStatusStore();
              const mergedCreds = (deviceState.credentials || []).map((cred) => ({
                ...cred,
                status: cred.status || statusStore[cred.id] || 'Active', // Prefer cached status
              }));
              logger.log(`[useCredentialEvents] Setting ${mergedCreds.length} credentials in UI`);
              logger.log(
                `[useCredentialEvents] Credentials patronIds:`,
                mergedCreds.map((c) => c.patronId)
              );
              setCredentials(mergedCreds);

              // Force fetch patron data for any new credentials that don't have patron info yet
              const newPatronIds = mergedCreds
                .map((c) => c.patronId)
                .filter((pid) => pid && !currentPatronsMap[pid]);
              if (newPatronIds.length > 0) {
                logger.log(
                  `[useCredentialEvents] Fetching patron data for new patronIds:`,
                  newPatronIds
                );
              }
            } else {
              logger.log(
                `[useCredentialEvents] Device ${deviceId} is NOT the current device (${selectedDeviceId}), cache updated only`
              );
            }
          } else {
            logger.warn(`[useCredentialEvents] No state returned for device ${deviceId}`);
          }
        } catch (error) {
          logger.error(
            `[useCredentialEvents] Error refreshing device ${deviceId}:`,
            error
          );
        }
      }
      logger.log('[useCredentialEvents] Finished processing all devices');

      // Update state to notify PatronCredentialsModal after we've extracted patronId
      if (provisionedPatronId) {
        logger.log(
          `[useCredentialEvents] Notifying modal: credential ${credentialId} for patron ${provisionedPatronId}`
        );
        setLastCredentialProvisioned({
          credentialId,
          patronId: provisionedPatronId,
          deviceIds,
          timestamp: Date.now(),
        });
      } else {
        logger.warn(
          `[useCredentialEvents] Could not determine patronId for credential ${credentialId}`
        );
      }
    })();
  }, [
    selectedDeviceId,
    fetchDeviceStateReadOnly,
    invalidateDeviceStateCache,
    updateDeviceState,
    setCredentials,
    setLastCredentialProvisioned,
  ]);

  /**
   * Handle credentialRemovedFromDevice event
   * Processes credential deletion from a specific device
   * 
   * @param {Object} message - WebSocket message
   * @param {string} message.deviceId - Device ID
   * @param {string} message.credentialId - Credential ID
   * @param {string} message.patronId - Patron ID (optional, may need lookup)
   * @param {number} message.remainingDeviceCount - Remaining devices with this credential
   */
  const handleCredentialRemovedFromDevice = useCallback((message) => {
    const { deviceId, credentialId, remainingDeviceCount } = message;
    
    if (!deviceId) {
      logger.warn('[useCredentialEvents] credentialRemovedFromDevice: No deviceId in message:', message);
      return;
    }

    logger.log('=== [useCredentialEvents] credentialRemovedFromDevice START ===');
    logger.log('[useCredentialEvents] Received message:', JSON.stringify(message, null, 2));
    logger.log(`[useCredentialEvents] Device: ${deviceId}, Credential: ${credentialId}`);
    logger.log(`[useCredentialEvents] Remaining device count: ${remainingDeviceCount}`);

    // Invalidate cache for affected device
    invalidateDeviceStateCache(deviceId);
    logger.log(`[useCredentialEvents] Current device: ${selectedDeviceId}`);
    logger.log(`[useCredentialEvents] Is current device: ${deviceId === selectedDeviceId}`);

    // Look up patronId from current credentials or device state cache
    // Read store snapshots at event-handling time (not as reactive deps)
    const currentCredentials = useCredentialsStore.getState().credentials;
    const currentDeviceStates = useDevicesStore.getState().deviceStates;
    let removedPatronId = message.patronId;
    if (!removedPatronId) {
      // Try to find the credential in current device's credentials
      const credInCurrentDevice = currentCredentials.find((c) => c.id === credentialId);
      if (credInCurrentDevice) {
        removedPatronId = credInCurrentDevice.patronId;
        logger.log(
          `[useCredentialEvents] Found patronId ${removedPatronId} from current device credentials`
        );
      } else {
        // Try to find in device state cache
        const cachedDeviceState = currentDeviceStates[deviceId];
        if (cachedDeviceState?.credentials) {
          const credInCache = cachedDeviceState.credentials.find((c) => c.id === credentialId);
          if (credInCache) {
            removedPatronId = credInCache.patronId;
            logger.log(
              `[useCredentialEvents] Found patronId ${removedPatronId} from device state cache`
            );
          }
        }
      }
    }

    // Update state to notify PatronCredentialsModal
    if (removedPatronId) {
      setLastCredentialRemoved({
        credentialId,
        patronId: removedPatronId,
        deviceId,
        remainingDeviceCount,
        timestamp: Date.now(),
      });
      logger.log(
        `[useCredentialEvents] Notifying modal: credential ${credentialId} for patron ${removedPatronId}`
      );
    } else {
      logger.warn(
        `[useCredentialEvents] Could not determine patronId for credential ${credentialId}`
      );
    }

    // Remove from pending deletion tracking
    const pendingKey = makePendingKey(deviceId, credentialId);

    logger.log(`[useCredentialEvents] 🔍 BEFORE UPDATE:`);
    logger.log(`[useCredentialEvents] - pendingKey: ${pendingKey}`);
    logger.log(
      `[useCredentialEvents] - pendingDeletionCredentials:`,
      Array.from(pendingDeletionCredentials)
    );
    logger.log(
      `[useCredentialEvents] - devicesWithPendingDeletions:`,
      Array.from(devicesWithPendingDeletions)
    );
    logger.log(`[useCredentialEvents] - Timestamp: ${new Date().toISOString()}`);

    setPendingDeletionCredentials((prev) => {
      logger.log(`[useCredentialEvents] 📝 Inside setPendingDeletionCredentials callback`);
      logger.log(`[useCredentialEvents] - prev set:`, Array.from(prev));

      const updated = new Set(prev);
      const wasDeleted = updated.delete(pendingKey);
      logger.log(
        `[useCredentialEvents] ✅ Removed ${pendingKey} from pending deletions (existed: ${wasDeleted})`
      );
      logger.log(`[useCredentialEvents] - Updated pending set:`, Array.from(updated));

      // Also remove from offline device credentials if present
      setOfflineDeviceCredentials((prevOffline) => {
        const updatedOffline = new Set(prevOffline);
        const wasOffline = updatedOffline.delete(pendingKey);
        if (wasOffline) {
          logger.log(
            `[useCredentialEvents] 🟢 Removed ${pendingKey} from offline devices (device came back online)`
          );
        }
        return updatedOffline;
      });

      // Check if this device has any other pending deletions AFTER removing this one
      const otherPendingKeys = Array.from(updated).filter(
        (key) => parsePendingKey(key).deviceId === deviceId
      );
      const hasOtherPendingDeletions = otherPendingKeys.length > 0;

      logger.log(
        `[useCredentialEvents] 🔍 Checking for other pending deletions on device ${deviceId}:`
      );
      logger.log(`[useCredentialEvents] - Other pending keys:`, otherPendingKeys);
      logger.log(
        `[useCredentialEvents] - hasOtherPendingDeletions: ${hasOtherPendingDeletions}`
      );

      if (!hasOtherPendingDeletions) {
        // No more pending deletions for this device, re-enable delete buttons
        logger.log(
          `[useCredentialEvents] 🟢 No more pending deletions for device ${deviceId}, re-enabling delete buttons NOW`
        );
        setDevicesWithPendingDeletions((prevDevices) => {
          logger.log(`[useCredentialEvents] 📝 Inside setDevicesWithPendingDeletions callback`);
          logger.log(`[useCredentialEvents] - prevDevices:`, Array.from(prevDevices));

          const updatedDevices = new Set(prevDevices);
          const wasInSet = updatedDevices.delete(deviceId);
          logger.log(
            `[useCredentialEvents] ✅ Removed device ${deviceId} from devicesWithPendingDeletions (existed: ${wasInSet})`
          );
          logger.log(`[useCredentialEvents] - Updated devices set:`, Array.from(updatedDevices));
          logger.log(
            `[useCredentialEvents] 🎉 STATE UPDATE COMPLETE - Buttons should re-enable on next render`
          );
          return updatedDevices;
        });
      } else {
        logger.log(
          `[useCredentialEvents] 🔴 Device ${deviceId} still has ${otherPendingKeys.length} pending deletion(s), keeping delete buttons disabled`
        );
      }

      return updated;
    });

    // Update cache by removing the credential directly (don't wait for API)
    setDeviceStates((prevStates) => {
      logger.log(
        `[useCredentialEvents] Cache update - checking prevStates for device ${deviceId}`
      );
      const deviceData = prevStates[deviceId];
      logger.log(`[useCredentialEvents] Device data exists: ${!!deviceData}`);
      logger.log(`[useCredentialEvents] Device credentials exist: ${!!deviceData?.credentials}`);

      if (deviceData && deviceData.credentials) {
        logger.log(
          `[useCredentialEvents] Current credentials in cache:`,
          deviceData.credentials.map((c) => ({ id: c.id, patronName: c.patronName }))
        );

        const updatedCredentials = deviceData.credentials.filter((cred) => {
          const keep = cred.id !== credentialId;
          logger.log(
            `[useCredentialEvents] Credential ${cred.id} (${cred.patronName}): ${
              keep ? 'KEEPING' : 'REMOVING'
            }`
          );
          return keep;
        });

        logger.log(
          `[useCredentialEvents] ✅ Removed credential ${credentialId} from cache. Before: ${deviceData.credentials.length}, After: ${updatedCredentials.length}`
        );
        logger.log(
          `[useCredentialEvents] Updated credentials in cache:`,
          updatedCredentials.map((c) => ({ id: c.id, patronName: c.patronName }))
        );

        const timestamp = Date.now();
        const updated = {
          ...prevStates,
          [deviceId]: {
            ...deviceData,
            credentials: updatedCredentials,
            lastUpdated: timestamp, // Mark as fresh from WebSocket
          },
        };

        logger.log(`[useCredentialEvents] Cache marked with timestamp: ${timestamp}`);

        // If this is the currently selected device, also update the UI state immediately
        if (deviceId === selectedDeviceId) {
          logger.log(
            '[useCredentialEvents] 🎯 This IS the current device - updating UI immediately'
          );
          const statusStore = getStatusStore();
          const mergedCreds = updatedCredentials.map((cred) => ({
            ...cred,
            status: cred.status || statusStore[cred.id] || 'Active', // Prefer cached status
          }));
          logger.log(
            `[useCredentialEvents] 🔄 Calling setCredentials with ${mergedCreds.length} credentials`
          );
          logger.log(
            `[useCredentialEvents] Credentials being set in UI:`,
            mergedCreds.map((c) => ({ id: c.id, patronName: c.patronName }))
          );
          setCredentials(mergedCreds);
          logger.log('[useCredentialEvents] ✅ setCredentials called');
        } else {
          logger.log(
            `[useCredentialEvents] ⏭️  Device ${deviceId} is NOT the current device (${selectedDeviceId}), cache updated only`
          );
        }

        logger.log('=== [useCredentialEvents] credentialRemovedFromDevice END - Cache Updated ===');
        return updated;
      } else {
        logger.warn(
          `[useCredentialEvents] ⚠️  No cached data for device ${deviceId}, will fetch on next view`
        );
        logger.log('=== [useCredentialEvents] credentialRemovedFromDevice END - No Cache ===');
        return prevStates;
      }
    });

    // If credentialRemovedFromDevice arrives before lastActionUpdated (RESOLVED) due to
    // SQS non-deterministic ordering, mark any PENDING/IN_PROGRESS action on this device
    // as RESOLVED. The lock system prevents concurrent device operations, so the only
    // PENDING action in history for this device is the one that triggered this event.
    if (setActionHistory) {
      setActionHistory((prev) =>
        prev.map((action) =>
          action.deviceId === deviceId &&
          (action.actionStatus === "PENDING" || action.actionStatus === "IN_PROGRESS")
            ? { ...action, actionStatus: "RESOLVED", lastUpdatedAt: new Date().toISOString() }
            : action
        )
      );
    }
  }, [
    selectedDeviceId,
    pendingDeletionCredentials,
    devicesWithPendingDeletions,
    invalidateDeviceStateCache,
    setLastCredentialRemoved,
    setPendingDeletionCredentials,
    setOfflineDeviceCredentials,
    setDevicesWithPendingDeletions,
    setDeviceStates,
    setCredentials,
    setActionHistory,
  ]);

  /**
   * Handle credentialDownloaded event
   * Tracks credential download events for UI feedback
   * 
   * @param {Object} message - WebSocket message
   * @param {string} message.credentialId - Credential ID
   * @param {string} message.patronId - Patron ID
   * @param {string} message.orgId - Organization ID
   */
  const handleCredentialDownloaded = useCallback((message) => {
    logger.log('[useCredentialEvents] credentialDownloaded:', message);
    
    // Update state to trigger PatronCredentialsModal refresh
    setLastCredentialDownload({
      credentialId: message.credentialId,
      patronId: message.patronId,
      orgId: message.orgId,
      timestamp: Date.now(),
    });
  }, [setLastCredentialDownload]);

  /**
   * Handle patronUpdated event
   * Syncs patron data changes to patronsMap
   * 
   * @param {Object} message - WebSocket message
   * @param {string} message.patronId - Patron ID
   * @param {string} message.orgId - Organization ID
   * @param {Object} message.patron - Updated patron object
   */
  const handlePatronUpdated = useCallback((message) => {
    const { patronId, patron } = message;
    logger.log(`[useCredentialEvents] patronUpdated: patronId=${patronId}`);

    // Update the patron in the patronsMap
    if (patronId && patron) {
      setPatronsMap((prevMap) => ({
        ...prevMap,
        [patronId]: patron,
      }));
      logger.log(`[useCredentialEvents] Updated patron ${patronId} in patronsMap`);
    }
  }, [setPatronsMap]);

  /**
   * Handle credentialProvisionRejected event
   * YP rejected the credential provisioning action (e.g. device offline, action superseded).
   * Clears the pending-addition state so the UI accurately reflects that the credential
   * is NOT on the device, and notifies the user.
   *
   * @param {Object} message - WebSocket message
   * @param {string} message.credentialId - Credential ID
   * @param {string} message.deviceId - Device the provisioning was attempted on
   * @param {string} message.reason - Human-readable rejection reason from YP
   */
  const handleCredentialProvisionRejected = useCallback((message) => {
    const { credentialId, deviceId: rejectedDeviceId, reason } = message;

    logger.log(
      `[useCredentialEvents] credentialProvisionRejected: credential=${credentialId}, device=${rejectedDeviceId}, reason=${reason}`
    );

    if (credentialId && rejectedDeviceId) {
      const pendingKey = makePendingKey(rejectedDeviceId, credentialId);

      // Remove from pending additions — provisioning failed, credential is NOT on the device
      setPendingAdditionCredentials((prev) => {
        const updated = new Set(prev);
        updated.delete(pendingKey);
        logger.log(`[useCredentialEvents] Removed ${pendingKey} from pendingAdditionCredentials`);
        return updated;
      });
    }

    // Notify the user so they know the add failed and can retry
    if (showNotification) {
      showNotification({
        message: `Credential could not be added to device: ${reason || 'rejected by device'}`,
        type: 'error',
      });
    }
  }, [setPendingAdditionCredentials, showNotification]);

  // Return memoized event handlers object to prevent unnecessary identity changes
  // that would cascade through useWebSocketMessageRouter's dependency array
  return useMemo(() => ({
    handleCredentialStatusChanged,
    handleCredentialProvisioned,
    handleCredentialRemovedFromDevice,
    handleCredentialDownloaded,
    handlePatronUpdated,
    handleCredentialProvisionRejected,
  }), [
    handleCredentialStatusChanged,
    handleCredentialProvisioned,
    handleCredentialRemovedFromDevice,
    handleCredentialDownloaded,
    handlePatronUpdated,
    handleCredentialProvisionRejected,
  ]);
}
